import { useCallback, useEffect, useState } from "react";
import { Button, Label } from "reactstrap";
import { toast } from "react-toastify";
import { formatDateTime } from "../../../../Utils/formatDateTime";
import {
    cancelLeadAgreementService,
    getLeadAgreementsService,
    sendLeadAgreementService,
} from "../../../../Services/CRMAdminServices/CRMTaskListService";

/**
 * The Business Builder agreement, on the lead screen.
 *
 * This is Fintrack's own e-signature, not DocuSign. Pressing Send builds the
 * agreement from the lead, emails a private link to the contact, and cancels
 * any earlier unsigned agreement on this lead so an old link cannot be signed
 * after the fees have changed.
 *
 * Only shown on MB leads, because it is the only kind of lead this agreement
 * belongs to.
 */

type Agreement = {
    agreementId: number;
    status: string;
    companyName?: string | null;
    signerName?: string | null;
    signerEmail?: string | null;
    setupFee?: number | null;
    monthlyFee?: number | null;
    termMonths?: number | null;
    sentOn: string;
    viewedOn?: string | null;
    signedOn?: string | null;
    signedName?: string | null;
    signedIp?: string | null;
    signingUrl?: string | null;
};

/* The API sends UTC without a marker, so the shared formatter is what turns
   it back into Melbourne time - a send at 11pm read as 1pm otherwise. */
const dt = (value?: string | null) => (value ? formatDateTime(value, "") : "");

/* Richard, 11 Oct 2026: a better layout for this panel - he chose the progress steps.
   Sent, Opened and Signed are three steps with the date under each, so where the
   proposal is reads at a glance instead of out of one small line of text.

   01/10/2026 9:55am, the way he writes a time. The shared formatter gives
   "01/10/2026, 09:55" in Melbourne time; this only reshapes it. */
const when = (value?: string | null) => {
    const s = dt(value);
    const m = /^(\d{2}\/\d{2}\/\d{4}),?\s+(\d{1,2}):(\d{2})/.exec(s);
    if (!m) return s;
    const h = Number(m[2]) % 24;
    return `${m[1]} ${h % 12 === 0 ? 12 : h % 12}:${m[3]}${h < 12 ? "am" : "pm"}`;
};

/* Whole days since it was sent. Same UTC rule as the formatter: the API's time
   carries no marker. */
const daysSince = (value?: string | null): number | null => {
    if (!value) return null;
    const s = String(value).trim();
    const iso = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s.replace(" ", "T")}Z`;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const GREEN = "#1a7f37";
const GREY = "#5d6370";
const BLUE = "#1a2fd6";

/* The words in the pill, top right. "Viewed" is the API's word; "Opened" is his. */
const pill = (status?: string | null) => {
    switch ((status || "").toLowerCase()) {
        case "signed": return { text: "Signed", color: "#ffffff", background: GREEN };
        case "viewed": return { text: "Opened, not signed", color: "#7a5200", background: "#fff1c2" };
        case "sent": return { text: "Sent, not opened", color: "#0b3d91", background: "#dbe7ff" };
        case "cancelled": return { text: "Cancelled", color: "#444444", background: "#e9eaee" };
        default: return { text: "Not sent", color: "#444444", background: "#e9eaee" };
    }
};

const Step = ({ done, last, title, detail }: { done: boolean; last?: boolean; title: string; detail: string }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
            {done ? (
                <div style={{
                    width: "26px", height: "26px", borderRadius: "50%", background: GREEN, flex: "0 0 auto",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#ffffff"
                        strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 7.5l3 3 6-7" /></svg>
                </div>
            ) : (
                <div style={{
                    width: "26px", height: "26px", boxSizing: "border-box", borderRadius: "50%",
                    background: "#ffffff", border: "3px solid #b3b8c4", flex: "0 0 auto",
                }} />
            )}
            {!last && <div style={{ height: "3px", flexGrow: 1, background: "#d5d8e0" }} />}
        </div>
        <div style={{ fontSize: "14px", fontWeight: 800, color: done ? "#222222" : GREY }}>{title}</div>
        <div style={{ fontSize: "13px", color: done ? "#555555" : GREY, overflowWrap: "anywhere" }}>{detail}</div>
    </div>
);

const LeadAgreement = ({ leadId, formData }: { leadId: any; formData: any }) => {
    const [agreements, setAgreements] = useState<Agreement[]>([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [showEarlier, setShowEarlier] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [autoTried, setAutoTried] = useState(false);

    /* One document only. The proposal is the deck: its fees, its term and
       everything included are printed on the slides, so there is nothing here
       to fill in. */
    const docType = "PROPOSAL";
    const docWord = "proposal";

    const load = useCallback(async () => {
        if (!leadId) return;
        setLoading(true);
        try {
            const response: any = await getLeadAgreementsService(leadId);
            setAgreements(response?.data ?? []);
        } catch {
            // A lead with no agreements is the normal case, and a failure here
            // must not get in the way of editing the lead.
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [leadId]);

    useEffect(() => { load(); }, [load]);

    const latest = agreements.length > 0 ? agreements[0] : null;

    const handleSend = async () => {
        const companyName = (formData?.companyName || "").trim();
        const email = (formData?.email || "").trim();

        if (!companyName) {
            toast.error("Add the company name before sending this.");
            return;
        }
        if (!email) {
            toast.error("Add the contact email before sending this.");
            return;
        }

        setSending(true);
        try {
            const response: any = await sendLeadAgreementService(leadId, {
                agreementType: docType,
                companyName,
                abn: (formData?.abn || "").trim() || null,
                acn: (formData?.acn || "").trim() || null,
                signerName: `${formData?.firstName || ""} ${formData?.surname || ""}`.trim() || null,
                signerEmail: email,
                sendEmail: true,
            });

            if (response?.data?.emailSent === false) {
                toast.warn(`The ${docWord} was created, but the email did not go out. Copy the link and send it yourself.`);
            } else {
                toast.success(`The ${docWord} has gone to ${email}.`);
            }
            await load();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || `Could not send the ${docWord}.`);
        } finally {
            setSending(false);
        }
    };

    /* APPROVE & SEND FROM SONIA'S EMAIL.  Richard, 12 Oct 2026: a broker who says yes to
       the trial by text gives Sonia his business name and email; she writes both on the
       lead and emails Richard a link to this screen ending ?send=proposal. Opening it is
       the approval: the proposal goes, exactly as if Send had been pressed. The link only
       works once - the flag comes off the address - and a proposal sent in the last 24
       hours is not sent again. */
    useEffect(() => {
        if (autoTried || !loaded || sending) return;
        if (new URLSearchParams(window.location.search).get("send") !== "proposal") return;
        if (!((formData?.email || "").trim() && (formData?.companyName || "").trim())) return;
        setAutoTried(true);
        try {
            const u = new URL(window.location.href);
            u.searchParams.delete("send");
            window.history.replaceState(null, "", u.toString());
        } catch { /* the address is cosmetic */ }
        const recent = latest && (latest.status || "").toLowerCase() !== "cancelled"
            && Date.now() - new Date(latest.sentOn).getTime() < 24 * 3600 * 1000;
        if (recent) {
            toast.info(`This ${docWord} was already sent in the last 24 hours, so it was not sent again.`);
            return;
        }
        handleSend();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoTried, loaded, sending, formData, latest]);

    const handleCancel = async (agreementId: number) => {
        try {
            await cancelLeadAgreementService(agreementId);
            toast.success("Agreement cancelled.");
            await load();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Could not cancel that agreement.");
        }
    };

    const status = (latest?.status || "").toLowerCase();
    const live = status === "sent" || status === "viewed" || status === "signed";   // not cancelled, not absent
    const p = pill(latest?.status);
    const waited = daysSince(latest?.sentOn);
    const earlier = agreements.slice(1);
    const earlierShown = showEarlier ? earlier : earlier.slice(0, 1);

    const openPdf = () =>
        /* Richard, 9 Oct 2026: "Proposal PDF". Opens the published PDF, not the signing
           link - opening the signing link from here would count as the client reading
           it and send the "Opened" email. */
        window.open("https://app.fintrack.com.au/proposal/proposal.pdf", "_blank", "noopener");

    const button = { height: "40px", padding: "0 16px", fontSize: "14px", flex: "0 0 auto" } as const;

    return (
        /* minWidth 0 and the wrapping are what keep this inside its half of the row.
           Without them a long line pushes the panel wider than the column and the
           buttons on the right fall off the page. */
        <div style={{
            border: "1px solid #c9d1f5", borderRadius: "6px", padding: "16px 18px",
            marginTop: "10px", minWidth: 0, maxWidth: "100%", overflow: "hidden",
            display: "flex", flexDirection: "column", gap: "16px",
        }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "8px 12px", minWidth: 0 }}>
                <Label className="mb-0" style={{ fontSize: "16px", fontWeight: 800, color: BLUE }}>
                    Business Builder proposal
                </Label>
                <span style={{
                    fontSize: "13px", fontWeight: 800, whiteSpace: "nowrap", flex: "0 0 auto",
                    color: p.color, background: p.background, borderRadius: "999px", padding: "4px 12px",
                }}>
                    {p.text}
                </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", minWidth: 0 }}>
                <Step done={live} title="Sent" detail={live ? when(latest?.sentOn) : "Not yet"} />
                <Step done={live && !!latest?.viewedOn} title="Opened"
                    detail={live && latest?.viewedOn ? when(latest.viewedOn) : (live ? "Not yet" : "-")} />
                <Step last done={status === "signed"} title="Signed"
                    detail={status === "signed"
                        ? `${when(latest?.signedOn)}${latest?.signedName ? ` by ${latest.signedName}` : ""}${latest?.signedIp ? ` from ${latest.signedIp}` : ""}`
                        : !live ? "-"
                            : waited === null || waited === 0 ? "Waiting"
                                : `Waiting, ${waited} day${waited === 1 ? "" : "s"}`} />
            </div>

            <div style={{ fontSize: "13px", color: "#555", minWidth: 0 }}>
                {live ? "Sent to " : "Goes to "}
                <strong style={{ color: "#222", wordBreak: "break-all" }}>
                    {(live && latest?.signerEmail) || formData?.email || "no email on this lead"}
                </strong>
            </div>

            <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px", minWidth: 0 }}>
                <Button
                    className="btn-gameplan" disabled={sending} onClick={handleSend} style={{ ...button, fontWeight: 800 }}
                    title="Emails a private signing link. Any earlier unsigned one is cancelled."
                >
                    {sending ? "Sending . . ." : latest ? "Send again" : `Send the ${docWord}`}
                </Button>
                <Button color="primary" outline onClick={openPdf} style={{ ...button, fontWeight: 700 }}>
                    Proposal PDF
                </Button>
                {latest && (status === "sent" || status === "viewed") && (
                    <Button color="danger" outline onClick={() => handleCancel(latest.agreementId)} style={{ ...button, fontWeight: 700 }}>
                        Cancel
                    </Button>
                )}
            </div>

            {!loading && earlier.length > 0 && (
                <div style={{ borderTop: "1px solid #e6e8ef", paddingTop: "10px", fontSize: "13px", color: "#555", minWidth: 0 }}>
                    <div className="d-flex align-items-start justify-content-between flex-wrap" style={{ gap: "6px 12px" }}>
                        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                            {earlierShown.map((a, i) => (
                                <div key={a.agreementId} style={{ marginTop: i ? "4px" : 0 }}>
                                    {i === 0 ? "Earlier: " : ""}
                                    <span style={{ fontWeight: 700, color: GREY }}>
                                        {(a.status || "").toLowerCase() === "viewed" ? "Opened" : a.status}
                                    </span>
                                    , sent {when(a.sentOn)}
                                    {a.viewedOn ? `, opened ${when(a.viewedOn)}` : ""}
                                    {a.signedOn ? `, signed by ${a.signedName} ${when(a.signedOn)}` : ""}
                                </div>
                            ))}
                        </div>
                        {earlier.length > 1 ? (
                            <button type="button" onClick={() => setShowEarlier(!showEarlier)}
                                style={{ border: 0, background: "none", padding: 0, color: BLUE, fontWeight: 700, fontSize: "13px", flex: "0 0 auto" }}>
                                {showEarlier ? "Show fewer" : `${earlier.length} earlier proposals`}
                            </button>
                        ) : (
                            <span style={{ color: BLUE, fontWeight: 700, flex: "0 0 auto" }}>1 earlier proposal</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadAgreement;
